import { useState, useEffect } from 'react';
import { api, endpoints } from '../config/api';
import { Modal, PageTitle, SpeakerCard } from '../components';
import { useConfig } from '../context/ConfigContext';
import { getAssetUrl } from '../config/assets';

function SpeakersPage() {
  const { config } = useConfig();
  const mujerKvUrl = config?.mujerKvUrl || getAssetUrl('mujerKv');
  const logoMedicina = config?.welcomeLogos?.[0]?.url || getAssetUrl('logoMedicina');
  const logoHeel = config?.welcomeLogos?.[1]?.url || getAssetUrl('logoHeel');
  const [event, setEvent] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const ev = await api.get(endpoints.eventActive());
        setEvent(ev);
        if (ev?.id) {
          const data = await api.get(endpoints.speakersByEvent(ev.id));
          setSpeakers(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        setError(err?.message || 'Error al cargar speakers');
        setSpeakers([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Cargando ponentes...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden pb-20">
      <div
        className="absolute inset-0 -z-10"
        style={{
          background: config?.bodyBackgroundImage
            ? `linear-gradient(180deg, rgba(15, 60, 50, 0.85) 0%, rgba(20, 80, 70, 0.75) 50%, rgba(25, 100, 90, 0.7) 100%), url(${config.bodyBackgroundImage}) center/cover`
            : 'linear-gradient(180deg, #0f3c32 0%, #145046 50%, #19645a 100%)',
        }}
      />

      <div className="relative z-0 flex flex-col flex-1 items-center justify-between px-4 pt-6">
        <div className="relative z-0 flex flex-col flex-col items-center justify-between px-4 pt-10">
          <div className="relative w-full overflow-hidden rounded-lg p-3 sm:p-4 flex items-center min-h-[180px] sm:min-h-[250px]">
            {config?.headerImage && (
              <div className="z-10 flex-1 min-w-0 pl-2 sm:pl-4 mr-12 sm:mr-20">
                <img src={config.headerImage} alt="37 Simposio" className="h-auto w-full max-w-[280px] sm:max-w-[326px] object-contain object-left" />
              </div>
            )}
            {mujerKvUrl && (
              <div className="absolute right-0 bottom-0 h-full w-1/3 sm:w-1/4 flex justify-center items-end pointer-events-none">
                <img src={mujerKvUrl} alt="" className="h-[100%] sm:h-[110%] w-auto object-contain object-bottom mix-blend-screen brightness-110 opacity-50 sm:opacity-60" />
              </div>
            )}
          </div>
        </div>

        <PageTitle>Speakers</PageTitle>

        <div className="flex-1 w-full max-w-2xl mx-auto p-4 overflow-y-auto">
          <div className="py-4">
            {error && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-amber-800">{error}</p>
              </div>
            )}
            {speakers.length === 0 && !error ? (
              <div className="py-8 text-center text-gray-500">
                No hay ponentes registrados.
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {speakers.map((s) => (
                  <SpeakerCard
                    key={s.id}
                    speaker={s}
                    variant="list"
                    onClick={() => setSelectedSpeaker(s)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="relative z-20 flex justify-between items-end px-4 py-6 pb-8 gap-4">
        <div className="flex flex-col items-start gap-2 min-w-0 flex-1">
          {logoMedicina && (
            <img src={logoMedicina} alt="Medicina Biorreguladora de Sistemas" className="h-[110%] w-auto sm:h-20 object-contain object-left max-w-[75%]" />
          )}
        </div>
        <div className="flex items-end flex-shrink-0">
          {logoHeel && (
            <img src={logoHeel} alt="Heel" className="h-8 sm:h-10 object-contain" />
          )}
        </div>
      </div>

      <Modal
        isOpen={!!selectedSpeaker}
        onClose={() => setSelectedSpeaker(null)}
        title={selectedSpeaker?.nombre || selectedSpeaker?.name || 'Ponente'}
        showCloseButton
        footerButtons={[]}
        size="lg"
        variant="dark"
        className="bg-gray-800/95 backdrop-blur-md text-white border border-white/20 shadow-2xl"
      >
        {selectedSpeaker && (
          <div className="space-y-4 text-white/95">
            {selectedSpeaker.profile?.startsWith('http') && (
              <div className="flex justify-center">
                <img
                  src={selectedSpeaker.profile}
                  alt={selectedSpeaker.nombre || selectedSpeaker.name}
                  className="w-24 h-24 rounded-full border-2 border-[#CFFF33] object-cover"
                />
              </div>
            )}
            {(selectedSpeaker.cargo || selectedSpeaker.profile) && (
              <p>
                <span className="font-semibold text-[#CFFF33]">Especialidad / Cargo:</span>{' '}
                {selectedSpeaker.cargo || selectedSpeaker.profile}
              </p>
            )}
            {selectedSpeaker.email && (
              <p>
                <span className="font-semibold text-[#CFFF33]">Correo:</span>{' '}
                <a href={`mailto:${selectedSpeaker.email}`} className="text-[#93f5e8] hover:underline">
                  {selectedSpeaker.email}
                </a>
              </p>
            )}
            {(selectedSpeaker.descripcion || selectedSpeaker.description) && (
              <p>
                <span className="font-semibold text-[#CFFF33] block mb-1">Descripción:</span>
                <span className="text-white/90">{selectedSpeaker.descripcion || selectedSpeaker.description}</span>
              </p>
            )}
            {Object.entries(selectedSpeaker)
              .filter(([key]) => !['id', 'nombre', 'name', 'profile', 'cargo', 'email', 'descripcion', 'description', 'eventoId', 'eventId'].includes(key))
              .map(([key, value]) => value != null && value !== '' && (
                <p key={key}>
                  <span className="font-semibold text-[#CFFF33] capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>{' '}
                  {String(value)}
                </p>
              ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default SpeakersPage;
